import {useState, useEffect, useMemo, useRef} from "react";
import { useSearchParams } from "react-router-dom";
import CardList from "../components/CardList";
import '../styles/Explore.css';
import Header from "../components/Header";
import Search from "../components/Search";
import { getABCollections } from "../lib/artblocks.ts";
import Select from "../components/base/Select";

const Explore = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL params or defaults
  const [searchFilter, setSearchFilter] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || "All");
  const [initCollections, setInitCollections] = useState([]);
  const [sortFilter, setSortFilter] = useState(searchParams.get('sort') || "Sort by Collection Id, ASC");
  // const [filteredCollections, setFilteredCollections] = useState([])

  const SortByType = (sortArray, sortType) => {
    // Create a copy to avoid mutation
    const sortedArray = [...sortArray];
    
    if(sortType === "Sort by Collection Id, ASC"){
      return sortedArray.sort((a, b) => a['projectId'] - b['projectId']);
    }
    if(sortType === "Sort by Collection Id, DESC"){
      return sortedArray.sort((a, b) => b['projectId'] - a['projectId']);
    }
    if(sortType === "Sort by Collection Name, ASC"){
      return sortedArray.sort((a, b) => (a['name'] || '').localeCompare(b['name'] || ''));
    }
    if(sortType === "Sort by Collection Name, DESC"){
      return sortedArray.sort((a, b) => (b['name'] || '').localeCompare(a['name'] || ''));
    }
    if(sortType === "Sort by Artist Name, ASC"){
      return sortedArray.sort((a, b) => (a['artistName'] || '').localeCompare(b['artistName'] || ''));
    }
    if(sortType === "Sort by Artist Name, DESC"){
      return sortedArray.sort((a, b) => (b['artistName'] || '').localeCompare(a['artistName'] || ''));
    }
    return sortedArray;
  }

  // Track previous filter values to detect actual changes (not initial mount)
  const prevFiltersRef = useRef({ searchFilter, statusFilter, sortFilter });
  
  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchFilter) params.set('search', searchFilter);
    if (statusFilter !== 'All') params.set('status', statusFilter);
    if (sortFilter !== 'Sort by Collection Id, ASC') params.set('sort', sortFilter);
    setSearchParams(params, { replace: true });
    
    // Clear stored scroll position only when filters actually change (not on initial mount)
    const filtersChanged = 
      prevFiltersRef.current.searchFilter !== searchFilter ||
      prevFiltersRef.current.statusFilter !== statusFilter ||
      prevFiltersRef.current.sortFilter !== sortFilter;
    
    if (filtersChanged && (prevFiltersRef.current.searchFilter !== undefined)) {
      // User actively changed filters, clear stored scroll position
      sessionStorage.removeItem('explore-scrollPosition');
      sessionStorage.removeItem('explore-visibleCollections');
    }
    
    prevFiltersRef.current = { searchFilter, statusFilter, sortFilter };
  }, [searchFilter, statusFilter, sortFilter, setSearchParams]);

  useEffect(()=>{
    (async() => {
      try {
        const collections = await getABCollections();
        setInitCollections(collections);
      } catch (error) {
        console.error("Error loading collections:", error);
        // You could set an error state here
      }
    })();
  },[]);

  const filteredCollections = useMemo(() => {
    let filteredCollections = [];
    if (initCollections) {
      if (statusFilter === "All"){
        filteredCollections = SortByType(initCollections.filter((collection)=> collection.complete || (!collection.complete && collection.active)), sortFilter);
      }
      else if (statusFilter === "Completed") {
        filteredCollections = SortByType(initCollections.filter((collection) => collection.complete), sortFilter);
      } else {
        const now = Date.now();
        const incompleteActive = initCollections.filter((collection) => !collection.complete && collection.active);

        if (statusFilter === "Upcoming") {
          filteredCollections = incompleteActive.filter((collection) => {
            const mintingDate = collection.mintingDate;
            const hasMinted = collection.invocations > 0;
            
            if (mintingDate && mintingDate * 1000 > now) {
              return true; // Minting hasn't started yet
            }
            if (!mintingDate && !hasMinted && !collection.activatedAt) {
              return true; // Never set up for minting
            }
            return false;
          });
          filteredCollections = SortByType(filteredCollections, sortFilter);
        } else if (statusFilter === "Open") {
          filteredCollections = incompleteActive.filter((collection) => {
            const mintingDate = collection.mintingDate;
            const hasMinted = collection.invocations > 0;
            
            if (collection.paused) {
              return false;
            }
            
            const hasStarted = mintingDate && mintingDate * 1000 <= now;
            if (hasStarted) {
              return true;
            }
            if (!mintingDate && (collection.activatedAt || hasMinted)) {
              return true;
            }
            return false;
          });
          filteredCollections = SortByType(filteredCollections, sortFilter);
        } else if (statusFilter === "Paused") {
          filteredCollections = SortByType(filteredCollections.filter(
            (collection) =>
              collection.paused &&
              ((collection.mintingDate && collection.mintingDate * 1000 < Date.now()) ||
                (collection.activatedAt && collection.activatedAt * 1000 < Date.now()))
          ), sortFilter);
        }
      }

      if (searchFilter) {
        filteredCollections = SortByType(filteredCollections
          .filter(
          (collection) => {
            // Add null/undefined checks before calling toLowerCase
            const searchLower = searchFilter.toLowerCase();
            const nameMatch = collection.name?.toLowerCase().includes(searchLower) || false;
            const artistMatch = collection.artistName?.toLowerCase().includes(searchLower) || false;
            const projectIdMatch = collection.projectId?.toString().includes(searchLower) || false;
            return nameMatch || artistMatch || projectIdMatch;
          }
        )
        , 
        sortFilter);
      }
      
      // DEBUG: Log V3 projects in filtered results
      const v3Contract = '0x99a9b7c1116f9ceeb1652de04d5969cce509b069';
      const v3InFiltered = filteredCollections.filter(c => 
        c.contractAddress?.toLowerCase() === v3Contract.toLowerCase()
      );
      console.log(`[Explore] Status: ${statusFilter}, Total filtered: ${filteredCollections.length}, V3 in filtered: ${v3InFiltered.length}`);
      if (v3InFiltered.length > 0) {
        console.log('V3 projects that will display:', v3InFiltered.slice(0, 3).map(c => ({
          projectId: c.projectId,
          name: c.name,
          complete: c.complete,
          active: c.active
        })));
      } else if (statusFilter === "All" || statusFilter === "Completed") {
        // Only warn if we expect to see V3 projects
        const v3InInit = initCollections.filter(c => 
          c.contractAddress?.toLowerCase() === v3Contract.toLowerCase()
        );
        console.warn(`[Explore] Expected V3 projects but none in filtered results. V3 in initCollections: ${v3InInit.length}`);
      }
    }

    return filteredCollections;
  }, [initCollections, statusFilter, searchFilter, sortFilter]);

  
  const changefilter = (filterValue) => {
    setSearchFilter(filterValue);
  }
  return (
    <div id="explore">
      <Header/>
      <div style={{paddingTop : "10%"}}>
        <Search changefilter={changefilter}/>
        <div style={{display : 'flex', flexDirection : 'row', justifyContent : 'space-around', padding : '50px'}}>
          <div style={{width : '20%'}}>
            <Select 
              initValue = {statusFilter}
              items = {['All', 'Completed', 'Upcoming', 'Open', 'Paused']}
              onChange = {(value)=>{setStatusFilter(value)}}
            />
          </div>
          <div style={{width : '20%'}}>
            <Select 
              initValue = {sortFilter}
              items = {
                ["Sort by Collection Id, ASC", 
                  "Sort by Collection Id, DESC", 
                  "Sort by Collection Name, ASC", 
                  "Sort by Collection Name, DESC",
                  "Sort by Artist Name, ASC",
                  "Sort by Artist Name, DESC"
                ]}
              onChange = {(value)=>{setSortFilter(value)}}
            />
          </div>
        </div>
      </div>
      <div id="list-container">
        <CardList 
          key={`${statusFilter}-${sortFilter}-${searchFilter}`}
          collections={filteredCollections} 
          type={"collection"}
        />
      </div>
    </div>
  );
};

export default Explore;
